import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Lightbulb, X, Receipt } from 'lucide-react';
import OrdersTable from '../components/prosthetist/OrdersTable';
import OrderDetails from '../components/common/OrderDetails';
import BillingModal from '../components/prosthetist/BillingModal';
import FeatureRequestsAdmin from '../components/admin/FeatureRequestsAdmin';
import { useOrders } from '../hooks/useOrders';
import { useAuth } from '../contexts/AuthContext';
import { formatPatientDisplay } from '../utils/nameNormalizer';

const ProsthetistDashboard = () => {
    const { user, isAdmin } = useAuth();

    // Filter orders by prosthetist_id (only if not admin)
    const orderFilters = useMemo(() => {
        if (isAdmin()) {
            return {}; // Admin sees all orders
        }
        return { prosthetistId: user?.id }; // Prosthetist only sees their orders
    }, [user?.id, isAdmin]);

    const { orders, loading, unreadCounts, handleStatusChange, handleSendMessage, handleMarkMessagesAsRead, handleWebSocketMessage, handleUpdateOrder, refetch } = useOrders(orderFilters);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showBilling, setShowBilling] = useState(false);
    const [activeTab, setActiveTab] = useState('orders');
    const [patientFilter, setPatientFilter] = useState({
        patientNameCode: '',
        clientName: ''
    });

    // Effect pour gérer les paramètres d'URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const patientNameCode = urlParams.get('patientNameCode');
        const clientName = urlParams.get('clientName');

        if (patientNameCode) {
            setPatientFilter({
                patientNameCode,
                clientName: clientName || ''
            });
        }
    }, []);

    const handlePatientClick = (patientInfo) => {
        const { patientNameCode, clientName } = patientInfo;

        // Fermer le modal OrderDetails si ouvert
        if (selectedOrder) {
            setSelectedOrder(null);
        }

        // Mettre à jour les filtres
        setPatientFilter({
            patientNameCode,
            clientName
        });

        // Mettre à jour l'URL
        const urlParams = new URLSearchParams();
        urlParams.set('patientNameCode', patientNameCode);
        if (clientName) {
            urlParams.set('clientName', clientName);
        }

        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.pushState({}, '', newUrl);
    };

    const clearFilters = () => {
        setPatientFilter({
            patientNameCode: '',
            clientName: ''
        });

        // Supprimer les paramètres d'URL
        window.history.pushState({}, '', window.location.pathname);
    };

    // Filtrer les commandes selon les critères de patient
    const filteredOrders = patientFilter.patientNameCode
        ? orders.filter(order =>
            order.patient_name_code?.toUpperCase() === patientFilter.patientNameCode.toUpperCase() &&
            (!patientFilter.clientName || order.client_name.includes(patientFilter.clientName))
          )
        : orders;

    // Calculer les statistiques sur les commandes filtrées
    const stats = {
        total: filteredOrders.length,
        pending: filteredOrders.filter(order => order.status === 'pending').length,
        inProgress: filteredOrders.filter(order => order.status === 'in_progress').length,
        shipped: filteredOrders.filter(order => order.status === 'shipped').length
    };

    const handleOrderClick = (order) => {
        setSelectedOrder(order);
    };

    const handleCloseOrderDetails = () => {
        setSelectedOrder(null);
    };

    // Synchroniser selectedOrder avec orders quand orders change
    // (ex: après envoi d'un message, les messages sont mis à jour dans orders)
    useEffect(() => {
        if (selectedOrder) {
            const updatedOrder = orders.find(o => o.id === selectedOrder.id);
            if (updatedOrder && updatedOrder !== selectedOrder) {
                setSelectedOrder(updatedOrder);
            }
        }
    }, [orders, selectedOrder]);

    if (loading) {
        return (
            <div className="min-h-screen">
                <div className="mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
                        <span className="ml-3 text-gray-400">Chargement des commandes...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-6">
            <div className="mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header moderne */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-2">
                                Tableau de bord
                            </h1>
                            <p className="text-gray-400 text-lg">
                                Gérez et suivez l'état de vos commandes reçues des cabinets dentaires
                            </p>
                        </div>
                        <button
                            onClick={() => setShowBilling(true)}
                            className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <Receipt className="h-5 w-5" />
                            Facturation
                        </button>
                    </div>
                </div>

                {/* Admin Tabs */}
                {isAdmin() && (
                    <div className="mb-8">
                        <div className="border-b border-white/10">
                            <nav className="-mb-px flex space-x-8">
                                <button
                                    onClick={() => setActiveTab('orders')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        activeTab === 'orders'
                                            ? 'border-yellow-500 text-yellow-400'
                                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-white/15'
                                    }`}
                                >
                                    <FileText className="h-4 w-4 inline mr-2" />
                                    Commandes
                                </button>
                                <button
                                    onClick={() => setActiveTab('feature-requests')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                        activeTab === 'feature-requests'
                                            ? 'border-yellow-500 text-yellow-400'
                                            : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-white/15'
                                    }`}
                                >
                                    <Lightbulb className="h-4 w-4 inline mr-2" />
                                    Demandes de fonctionnalités
                                </button>
                            </nav>
                        </div>
                    </div>
                )}

                {/* Tab Content */}
                {activeTab === 'feature-requests' && isAdmin() ? (
                    <FeatureRequestsAdmin />
                ) : (
                    <>
                        {/* Indicateur de filtrage */}
                        {patientFilter.patientNameCode && (
                            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <FileText className="h-5 w-5 text-yellow-400" />
                                        <div>
                                            <p className="text-sm font-medium text-yellow-300">
                                                Historique filtré pour le patient
                                            </p>
                                            <p className="text-lg font-semibold text-yellow-400">
                                                {patientFilter.patientNameCode}
                                            </p>
                                            {patientFilter.clientName && (
                                                <p className="text-sm text-yellow-400">
                                                    chez {patientFilter.clientName}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={clearFilters}
                                        className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-colors"
                                        title="Effacer le filtre"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                                <p className="text-sm text-yellow-400 mt-2">
                                    {stats.total} commande{stats.total > 1 ? 's' : ''} trouvée{stats.total > 1 ? 's' : ''}
                                </p>
                            </div>
                        )}

                {/* Tableau des commandes */}
                <div className="glass border border-white/10 rounded-2xl shadow-lg overflow-hidden">
                    <div className="bg-gradient-to-r from-yellow-500/15 to-transparent border-b border-white/[0.07] px-6 py-4">
                        <h2 className="text-xl font-semibold text-white">
                            Commandes reçues
                        </h2>
                        <p className="text-gray-300 text-sm mt-1">
                            Gérez l'état de vos commandes
                        </p>
                    </div>
                    <div className="p-6">
                        <OrdersTable
                            orders={filteredOrders}
                            onStatusChange={handleStatusChange}
                            onOrderClick={handleOrderClick}
                            onPatientClick={handlePatientClick}
                            unreadCounts={unreadCounts}
                        />
                    </div>
                </div>

                {/* Modal de détails de commande */}
                {selectedOrder && (
                    <OrderDetails
                        order={selectedOrder}
                        onClose={handleCloseOrderDetails}
                        onSendMessage={handleSendMessage}
                        onMarkMessagesAsRead={handleMarkMessagesAsRead}
                        onWebSocketMessage={handleWebSocketMessage}
                        onOrderUpdated={handleUpdateOrder}
                        onStatusChange={handleStatusChange}
                        currentUserType="prosthetist"
                        currentUserName={user?.name || 'Prothésiste'}
                        currentUserId={user?.id}
                        onPatientClick={handlePatientClick}
                    />
                )}
                    </>
                )}

                {/* Modal de facturation groupée */}
                {showBilling && (
                    <BillingModal
                        onClose={() => setShowBilling(false)}
                        onGenerated={refetch}
                    />
                )}
            </div>
        </div>
    );
};

export default ProsthetistDashboard;
